import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { Sparkles, ChevronDown, Loader2, Plus } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE } from '../config';
import { useCreditCheck } from '../hooks/useCreditCheck';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { cn } from '@/lib/utils';
import { Badge } from './ui/badge';
import { InlineEditableText } from './InlineEditableText';

const normalizeTag = (tag: string): string => tag.trim().toLowerCase();

interface AnalyzeResourceButtonProps {
  resourceId: string;
  projectId: string;
  mimeType?: string | null;
  isLinkResource?: boolean;
  hasExtractedText?: boolean;
  initialAnalysis?: ResourceAnalysis | null;
  initialAnalysisError?: string | null;
  resourceTags?: string[];
  readOnly?: boolean;
  onAnalysisComplete?: (analysis: ResourceAnalysis) => void;
  onAnalysisFailed?: () => void;
  onAnalysisUpdated?: (analysis: ResourceAnalysis) => void;
  onAddTagFromKeyword?: (keyword: string) => void | Promise<void>;
}

interface ResourceAnalysis {
  title: string;
  type: 'news' | 'article' | 'website' | 'book' | 'paper' | 'video' | 'Twitter' | 'Instagram';
  source: string;
  date: string;
  summary: string;
  keywords: string[];
  natural_id?: string;
}

type EditableAnalysisField = 'title' | 'type' | 'source' | 'date' | 'summary';

const analyzableMimeTypes = [
  'text/plain',
  'text/markdown',
  'text/html',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

export function formatResourceAnalysisErrorMessage(
  errorCode: string | null | undefined,
  t: (key: string) => string,
): string {
  const base = t('failed_to_analyze_resource');
  if (errorCode === 'RESOURCE_ANALYSIS_IMAGE_TOO_LARGE') {
    return `${base} ${t('resource_analysis_image_too_large')}`;
  }
  return base;
}

export const AnalyzeResourceButton: React.FC<AnalyzeResourceButtonProps> = ({
  resourceId,
  projectId,
  mimeType,
  isLinkResource,
  hasExtractedText,
  initialAnalysis,
  initialAnalysisError,
  resourceTags = [],
  readOnly = false,
  onAnalysisComplete,
  onAnalysisFailed,
  onAnalysisUpdated,
  onAddTagFromKeyword,
}) => {
  const resourceTagSet = React.useMemo(
    () => new Set(resourceTags.map(normalizeTag)),
    [resourceTags]
  );
  const { token } = useAuth();
  const { t } = useLocale();
  const { showErrorToast } = useToast();
  const { checkCredits, CreditLimitModalComponent } = useCreditCheck();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(
    initialAnalysisError ? formatResourceAnalysisErrorMessage(initialAnalysisError, t) : null
  );
  const [analysis, setAnalysis] = useState<ResourceAnalysis | null>(initialAnalysis || null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [analysisNotes, setAnalysisNotes] = useState<string>('');
  const [isSavingAnalysis, setIsSavingAnalysis] = useState(false);

  useEffect(() => {
    setAnalysis(initialAnalysis || null);
  }, [initialAnalysis]);

  useEffect(() => {
    if (initialAnalysisError) {
      setError(formatResourceAnalysisErrorMessage(initialAnalysisError, t));
    }
  }, [initialAnalysisError, t]);

  const canAnalyze = isLinkResource
    ? true
    : mimeType && analyzableMimeTypes.includes(mimeType);

  const persistAnalysisField = async (
    field: EditableAnalysisField,
    value: string,
  ): Promise<boolean> => {
    if (!token || !analysis || isSavingAnalysis) {
      return false;
    }

    const previousAnalysis = analysis;
    const nextAnalysis: ResourceAnalysis = {
      ...analysis,
      [field]: field === 'type'
        ? (value as ResourceAnalysis['type'])
        : value,
    };

    if (JSON.stringify(nextAnalysis) === JSON.stringify(previousAnalysis)) {
      return false;
    }

    setAnalysis(nextAnalysis);
    setIsSavingAnalysis(true);

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ analysis: nextAnalysis }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || 'Failed to update analysis');
      }

      onAnalysisUpdated?.(nextAnalysis);
      return true;
    } catch (err) {
      setAnalysis(previousAnalysis);
      const message = err instanceof Error ? err.message : t('failed_to_analyze_resource');
      showErrorToast(message);
      return false;
    } finally {
      setIsSavingAnalysis(false);
    }
  };

  if (!canAnalyze) {
    return null;
  }

  const handleAnalyze = async (notes?: string): Promise<void> => {
    if (!token || isAnalyzing) {
      return;
    }

    const hasCredits = await checkCredits();
    if (!hasCredits) {
      setIsNotesModalOpen(false);
      return;
    }

    const abortController = new AbortController();
    let timeoutId: NodeJS.Timeout | null = null;

    setIsAnalyzing(true);
    setError(null);

    try {
      timeoutId = setTimeout(() => {
        if (!abortController.signal.aborted) {
          abortController.abort();
        }
      }, 120000);

      const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}/analyze`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          analysisNotes: notes?.trim() || undefined,
        }),
        signal: abortController.signal,
      });

      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as {
          error?: string;
          errorCode?: string;
        };
        throw Object.assign(
          new Error(errorData.error || 'Failed to analyze resource'),
          { errorCode: errorData.errorCode }
        );
      }

      const data = await response.json();
      setAnalysis(data.analysis);
      if (onAnalysisComplete) {
        onAnalysisComplete(data.analysis);
      }
      setIsNotesModalOpen(false);
      setAnalysisNotes('');
    } catch (err) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (err instanceof Error && err.name === 'AbortError') {
        setError('El análisis tardó demasiado. Por favor, intenta de nuevo.');
      } else {
        console.error('Error analyzing resource:', err);
        const errorCode = (err as Error & { errorCode?: string }).errorCode;
        setError(formatResourceAnalysisErrorMessage(errorCode, t));
        onAnalysisFailed?.();
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAnalyzeWithNotes = (): void => {
    setIsNotesModalOpen(true);
  };

  const handleNotesModalConfirm = (): void => {
    handleAnalyze(analysisNotes);
  };

  const editTooltip = t('inline_editable_click_to_edit');

  const renderEditableField = (
    field: EditableAnalysisField,
    label: string,
    options?: { multiline?: boolean },
  ): React.ReactNode => {
    if (!analysis) {
      return null;
    }
    const value = analysis[field] ?? '';
    if (!value && readOnly) {
      return null;
    }

    return (
      <div className={options?.multiline ? 'flex flex-col gap-1' : 'flex min-w-0 flex-wrap items-baseline gap-1'}>
        <strong className="shrink-0">{label}:</strong>
        <InlineEditableText
          value={value}
          onCommit={(next) => persistAnalysisField(field, next)}
          savedFieldLabel={label}
          readOnly={readOnly}
          disabled={isSavingAnalysis}
          allowEmpty
          multiline={options?.multiline}
          rows={options?.multiline ? 4 : undefined}
          placeholder={label}
          tooltip={editTooltip}
          className={options?.multiline ? 'w-full font-normal text-foreground' : 'font-normal text-foreground'}
          displayClassName={options?.multiline ? 'w-full' : undefined}
        />
      </div>
    );
  };

  const analyzeButton = (
    <Button
      onClick={() => handleAnalyze()}
      disabled={isAnalyzing || readOnly}
      size="lg"
      variant="outline"
      className={cn('rounded-r-none border-r-0 flex-1')}
    >
      {isAnalyzing ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Sparkles className="h-4 w-4 mr-2" />
      )}
      {isAnalyzing ? t('analyzing') : t('analyze')}
    </Button>
  );

  return (
    <>
      {CreditLimitModalComponent}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {!readOnly && (
        <>
          <div style={{ display: 'flex', width: '100%', borderRadius: '0.5rem', overflow: 'hidden', backgroundColor: 'var(--card)' }}>
            {analyzeButton}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="lg"
                  variant="outline"
                  disabled={isAnalyzing}
                  className={cn('rounded-l-none px-2 border-l-0')}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleAnalyzeWithNotes}>
                  {t('analyze_with_notes')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="text-xs leading-snug text-muted-foreground">
            {t('analyze_resource_tooltip')}
          </p>
        </>
      )}
      {error && (
        <p style={{ color: '#ef4444', fontSize: '0.875rem' }}>{error}</p>
      )}
      {analysis && (
        <div style={{ marginTop: readOnly ? 0 : '1rem', padding: '1rem', backgroundColor: 'var(--card)', borderRadius: '0.5rem', border: '1px solid var(--border)' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
            {t('analysis')}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
            {renderEditableField('title', t('title'))}
            {renderEditableField('type', t('type'))}
            {renderEditableField('source', t('Source'))}
            {renderEditableField('date', t('date'))}
            {renderEditableField('summary', t('summary'), { multiline: true })}
            {analysis.keywords && analysis.keywords.length > 0 && (
              <div>
                <strong>{t('Keywords')}:</strong>
                <div className="mt-2 flex flex-wrap gap-2">
                  {analysis.keywords.map((keyword) => {
                    const isAlreadyTag = resourceTagSet.has(normalizeTag(keyword));
                    return (
                      <button
                        key={keyword}
                        type="button"
                        onClick={() => {
                          if (!isAlreadyTag && onAddTagFromKeyword && !readOnly) {
                            void onAddTagFromKeyword(keyword);
                          }
                        }}
                        className={isAlreadyTag || readOnly ? '' : 'group'}
                        style={{ cursor: isAlreadyTag || !onAddTagFromKeyword || readOnly ? 'default' : 'pointer' }}
                      >
                        <Badge
                          variant="outline"
                          className={cn(
                            'flex items-center gap-1',
                            isAlreadyTag
                              ? 'bg-muted text-muted-foreground border-muted-foreground/30'
                              : 'transition-colors group-hover:bg-secondary group-hover:text-secondary-foreground group-hover:border-transparent'
                          )}
                        >
                          <span>{keyword}</span>
                          {!isAlreadyTag && onAddTagFromKeyword && !readOnly && (
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
                              <Plus className="h-3 w-3" />
                            </span>
                          )}
                        </Badge>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <Dialog open={isNotesModalOpen} onOpenChange={setIsNotesModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('Analysis notes')}</DialogTitle>
            <DialogDescription>
              {t('Optional notes to guide the analysis...')}
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={analysisNotes}
            onChange={(e) => setAnalysisNotes(e.target.value)}
            placeholder={t('Optional notes to guide the analysis...')}
            disabled={isAnalyzing}
            autoFocus
            className="min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-y mb-6"
          />
          <DialogFooter>
            <CancelButton
              onClick={() => {
                setIsNotesModalOpen(false);
                setAnalysisNotes('');
              }}
              disabled={isAnalyzing}
            >
              {t('cancel')}
            </CancelButton>
            <Button
              onClick={handleNotesModalConfirm}
              disabled={isAnalyzing}
              dialogPrimary
              className="inline-flex items-center gap-1.5"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('analyzing')}
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  {t('analyze')}
                  <DialogPrimaryShortcut className="ml-0.5" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
